/*
 * Intelligems integration helpers for the Eden Brothers "Peak" (Ignite) theme.
 *
 * Two concerns, both kept out of the minified vendor code in global.js:
 *
 *   1) nice-select variant dropdowns (card quick-add): the theme renders each
 *      <option> as "{label} - ${price}" with the variant id on the <option>
 *      value. NiceSelect copies that whole string into an <li> with no dedicated
 *      price node, so Intelligems has a variant id but nothing clean to tag.
 *      We patch NiceSelect.default.prototype._renderItem (and _renderSelectedItems
 *      for the collapsed ".current" control) to wrap ONLY the trailing price in a
 *      <span class="igPrice" data-variant-id="{id}"> so IG can bind + update it.
 *
 *   2) cart compare-at subtotal: the theme only prints cart.total_price. When a
 *      test configures compare-at prices we show a struck-through "original"
 *      subtotal = sum(compare price x qty) using the IG price API. Liquid can't
 *      see test-configured compare prices, so this is computed client-side.
 *
 * Loaded from layout/theme.liquid immediately after global.js (deferred, so it
 * runs after NiceSelect is defined and before DOMContentLoaded fires
 * renderSelects). All patches are idempotent and defensive.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1) nice-select price node
   * ------------------------------------------------------------------ */

  // Wrap the trailing price substring of `el` in a bare, variant-bound node.
  // Leaves the rest of the label ("10 Bulbs / Ships in Fall - ") intact.
  function wrapTrailingPrice(el, variantId) {
    if (!el || !variantId) return;
    if (el.querySelector('[data-ig-price="1"]')) return; // already wrapped

    var text = el.textContent;
    // Trailing money token: currency symbol + digits/sep, optional " USD".
    // Anchored to end of string so only the price (not the label) is matched.
    var match = text.match(/([^\s\d]?\s?[\d][\d.,]*(?:\s?[A-Z]{3})?)\s*$/);
    if (!match) return;
    var price = match[1].trim();
    // Guard: require an actual currency-ish token (has a digit + a symbol or 3-letter code)
    if (!/[\d]/.test(price) || !/[^\d.,\s]/.test(price)) return;

    var idx = text.lastIndexOf(price);
    if (idx < 0) return;
    var before = text.slice(0, idx);

    el.textContent = before;
    var span = document.createElement('span');
    span.className = 'igPrice';
    span.setAttribute('data-variant-id', variantId);
    span.setAttribute('data-ig-price', '1');
    span.textContent = price;
    el.appendChild(span);
  }

  function patchNiceSelect() {
    var NS = window.NiceSelect;
    if (!NS || !NS.default || !NS.default.prototype) return false;
    var proto = NS.default.prototype;
    if (proto.__igPatched) return true;

    var origRenderItem = proto._renderItem;
    proto._renderItem = function (option) {
      var li = origRenderItem.call(this, option);
      try {
        if (li && option && option.attributes && !option.attributes.optgroup) {
          var variantId = (option.data && option.data.value) || li.getAttribute('data-value');
          wrapTrailingPrice(li, variantId);
        }
      } catch (e) { /* never break the theme's dropdown */ }
      return li;
    };

    var origRenderSelected = proto._renderSelectedItems;
    proto._renderSelectedItems = function () {
      origRenderSelected.call(this);
      try {
        if (!this.multiple && this.dropdown &&
            this.selectedOptions && this.selectedOptions.length > 0) {
          var current = this.dropdown.querySelector('.current');
          var variantId = this.selectedOptions[0].data && this.selectedOptions[0].data.value;
          wrapTrailingPrice(current, variantId);
        }
      } catch (e) { /* no-op */ }
    };

    proto.__igPatched = true;
    return true;
  }

  // Fallback sweep: if any dropdowns were built before the patch applied,
  // wrap their existing <li> items (keyed on data-value) and the .current node.
  function sweepExistingDropdowns() {
    document.querySelectorAll('.nice-select').forEach(function (dropdown) {
      dropdown.querySelectorAll('ul.nice-list li.option').forEach(function (li) {
        wrapTrailingPrice(li, li.getAttribute('data-value'));
      });
      var current = dropdown.querySelector('.current');
      var select = dropdown.previousElementSibling;
      if (current && select && select.tagName === 'SELECT' && select.value) {
        wrapTrailingPrice(current, select.value);
      }
    });
  }

  // Patch the prototype as early as possible so it is in place before the
  // theme's renderSelects() runs on DOMContentLoaded. global.js defines
  // NiceSelect synchronously and loads before this file, so patchNiceSelect()
  // normally succeeds on the first call; the retry loop only covers the case
  // where this script somehow executes first.
  function patchNiceSelectEarly() {
    if (patchNiceSelect()) return;
    var tries = 0;
    var iv = setInterval(function () {
      tries += 1;
      if (patchNiceSelect() || tries > 200) clearInterval(iv); // ~10s max
    }, 50);
  }

  /* ------------------------------------------------------------------ *
   * 2) re-scan trigger for dynamically-rendered prices
   *
   * Intelligems scans server-rendered prices at init but does NOT
   * automatically pick up price nodes that appear later (Rebuy Vue widgets,
   * the nice-select dropdown, cart section re-renders). window.igData.resetDom()
   * forces IG to re-scan and replace those. We debounce it and trigger it
   * whenever a new igPrice / Rebuy price node is ADDED to the DOM. We react only
   * to added elements (not to IG's own text rewrites), so there is no loop.
   * ------------------------------------------------------------------ */

  var resetDomPending = false;
  function igResetDom() {
    if (resetDomPending) return;
    resetDomPending = true;
    setTimeout(function () {
      resetDomPending = false;
      try {
        if (window.igData && typeof window.igData.resetDom === 'function') {
          window.igData.resetDom();
        }
      } catch (e) { /* no-op */ }
    }, 250);
  }

  var IG_PRICE_SELECTOR = '.igPrice, .igComparePrice, .rebuy-money';
  function initResetDomObserver() {
    igResetDom(); // catch anything already rendered before IG finished init
    var seen = new WeakSet();
    var obs = new MutationObserver(function (mutations) {
      var fresh = false;
      for (var i = 0; i < mutations.length && !fresh; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue; // elements only
          var hits = node.matches && node.matches(IG_PRICE_SELECTOR) ? [node] : [];
          if (node.querySelectorAll) {
            var found = node.querySelectorAll(IG_PRICE_SELECTOR);
            for (var k = 0; k < found.length; k++) hits.push(found[k]);
          }
          for (var h = 0; h < hits.length; h++) {
            if (!seen.has(hits[h])) { seen.add(hits[h]); fresh = true; }
          }
        }
      }
      if (fresh) igResetDom();
    });
    try { obs.observe(document.body, { childList: true, subtree: true }); } catch (e) { /* no-op */ }

    // Rebuy also fires its own lifecycle events — trigger a re-scan on those too.
    ['rebuy:widget.ready', 'rebuy:cart.ready', 'rebuy:smartcart.ready', 'rebuy:all.done', 'rebuy:widget.rendered']
      .forEach(function (evt) { document.addEventListener(evt, igResetDom); });
  }

  /* ------------------------------------------------------------------ *
   * 3) cart compare-at subtotal
   * ------------------------------------------------------------------ */

  function igPriceApi() {
    return (window.igData && window.igData.price) || null;
  }

  function toNumber(v) {
    var n = typeof v === 'string' ? parseFloat(v) : v;
    return (typeof n === 'number' && !isNaN(n)) ? n : 0;
  }

  function formatMoney(api, dollars) {
    // Prefer IG's own formatter to match the store's currency format.
    if (api && typeof api.formatMoney === 'function') {
      try { return api.formatMoney(dollars); } catch (e) { /* fall through */ }
    }
    if (api && typeof api.format === 'function') {
      try { return api.format(dollars); } catch (e) { /* fall through */ }
    }
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
    } catch (e) {
      return '$' + dollars.toFixed(2);
    }
  }

  function recomputeCompareSubtotals() {
    var api = igPriceApi();
    if (!api || typeof api.getPriceByVariantId !== 'function') return;
    var getCompare = typeof api.getComparePriceByVariantId === 'function'
      ? api.getComparePriceByVariantId.bind(api)
      : null;

    document.querySelectorAll('[data-ig-cart-root]').forEach(function (root) {
      var dataEl = root.querySelector('[data-ig-cart-lines]');
      var target = root.querySelector('[data-ig-compare-subtotal]');
      if (!dataEl || !target) return;

      var lines;
      try { lines = JSON.parse(dataEl.textContent || '[]'); } catch (e) { return; }
      if (!lines || !lines.length) { target.classList.add('hidden'); return; }

      var current = 0, compare = 0, hasCompare = false;
      lines.forEach(function (line) {
        var qty = toNumber(line.q) || 1;
        var price = toNumber(api.getPriceByVariantId(line.id));
        var cmp = getCompare ? toNumber(getCompare(line.id)) : 0;
        var unitCompare = cmp > price ? cmp : price;
        if (cmp > price) hasCompare = true;
        current += price * qty;
        compare += unitCompare * qty;
      });

      if (hasCompare && compare > current + 0.005) {
        var formatted = formatMoney(api, compare);
        if (target.textContent !== formatted) target.textContent = formatted;
        target.classList.remove('hidden');
      } else {
        target.classList.add('hidden');
      }
    });
  }

  // Debounce so bursts of cart DOM mutations collapse into one recompute.
  var rafPending = false;
  function scheduleRecompute() {
    if (rafPending) return;
    rafPending = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      rafPending = false;
      recomputeCompareSubtotals();
    }, 16);
  }

  function initCartSubtotal() {
    scheduleRecompute();

    // Recompute when the cart re-renders or IG rewrites prices. The observer
    // ignores mutations inside our own compare node to avoid feedback loops.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (t && t.closest && t.closest('[data-ig-compare-subtotal]')) continue;
        scheduleRecompute();
        return;
      }
    });
    try {
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    } catch (e) { /* no body yet is impossible at DCL, but stay safe */ }

    // Catch async IG data load: poll briefly until the price API is ready.
    var tries = 0;
    var iv = setInterval(function () {
      tries += 1;
      recomputeCompareSubtotals();
      if (igPriceApi() || tries > 40) clearInterval(iv); // ~10s
    }, 250);

    // Common theme cart events.
    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (evt) {
      document.addEventListener(evt, scheduleRecompute);
    });
  }

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  // Patch immediately (before renderSelects binds on DOMContentLoaded).
  patchNiceSelectEarly();

  // The rest needs the DOM: sweep any pre-built dropdowns, start the re-scan
  // observer, and wire up the cart.
  function onReady() {
    sweepExistingDropdowns();
    igResetDom(); // ensure IG re-scans the nice-select price nodes we just wrapped
    initResetDomObserver();
    initCartSubtotal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();